import { Injectable } from "@angular/core";
import { JobPosting, JobPostingData } from "../interfaces/job-posting";
import { map, Observable } from "rxjs";
import { HttpParams } from "@angular/common/http";
import { ServerDataSource } from "angular2-smart-table";
import { environment } from "../../../environments/environment";
import { HttpService } from "./common/http.service";
import { User } from "../interfaces/auth/users";
import { omit } from "lodash";
import { prepRecordForApi } from "../utils/utils.service";

@Injectable()
export class JobPostingsService extends JobPostingData {

  private readonly url: string;

  get gridDataSource(): ServerDataSource {
    return this.api.getServerDataSource(`${environment.API_BASE_URL}/${this.url}?populate=*`);
  }

  get gridArchivedDataSource(): ServerDataSource {
    return this.api.getServerDataSource(`${environment.API_BASE_URL}/${this.url}?filters[archivedOn][$notNull]=true`);
  }

  constructor(private api: HttpService) {
    super();
    this.url = 'job-postings';
  }

  getDataSourceOfClient(clientId: number): ServerDataSource {
    return this.api.getServerDataSource(`${environment.API_BASE_URL}/${this.url}?populate=*`);
  }

  listAll(): Observable<JobPosting[]> {
    let params: HttpParams = new HttpParams()
      .append('limit', '-1')
      .append('offset', '0')
      .append('populate', '*');

    return this.api.get(this.url, { params }).pipe(map(res => res.data || res));
  }

  list(pageNumber: number, pageSize: number, query: string = ''): Observable<JobPosting[]> {
    let params: HttpParams = new HttpParams()
      .append('with', 'deep,2');

    if (pageSize > 0) {
      params = params.append('limit', `${pageSize}`);
    }

    if (pageNumber > 0) {
      params = params.append('offset', `${(pageNumber - 1) * pageSize}`);
    }

    if (query) {
      params = params.append('where', query);
    }

    return this.api.get(this.url, { params }).pipe(map(res => res.data || res));
  }

  rawQry(pageNumber: number, pageSize: number, params: HttpParams):Observable<JobPosting[] | any> {
    if (pageSize > 0) {
      params = params.append('limit', `${pageSize}`);
    }

    if (pageNumber > 0) {
      params = params.append('offset', `${(pageNumber - 1) * pageSize}`);
    }

    // 'with' for population, 'where' for filters, 'order' for sorting

    return this.api.get(this.url, { params }).pipe(map(res => res.data || res));
  }

  get(id: any): Observable<JobPosting> {
    return this.api.get(`${this.url}/${id}`, { params: new HttpParams().append('populate', '*') })
      .pipe(map(res => res.data || res));
  }

  create(jobPosting: JobPosting): Observable<JobPosting> {
    let params: HttpParams = new HttpParams()
    jobPosting = prepRecordForApi(omit(jobPosting, ['link']));
    
    return this.api.post(`${this.url}?populate=*`, { data: jobPosting }, params)
      .pipe(map(res => res.data));
  }

  update(JobPosting: JobPosting): Observable<JobPosting> {
    let params: HttpParams = new HttpParams()
      .append('populate', '*');

    const changedJobPosting = prepRecordForApi(omit(JobPosting, ['link','id', 'documentId', 'createdAt', 'creator', 'lastUpdatedOn', 'updatedAt', 'archivedOn', 'archivedBy']));
    return this.api.put(`${this.url}/${JobPosting.documentId}?populate=*`, { data: changedJobPosting }, params)
      .pipe(map(res => res.data));
  }

  archive(JobPosting: JobPosting, date: Date, user: User): Observable<JobPosting> {
    return this.api.put(`${this.url}/${JobPosting.documentId}`, {
      data: {
        archivedOn: date,
        archivedBy: user,
      },
    });
  }

  unarchive(JobPosting: JobPosting): Observable<JobPosting> {
    return this.api.put(`${this.url}/${JobPosting.documentId}`, {
      data: {
        archivedOn: null,
        archivedBy: null,
      },
    });
  }

  delete(id: any): Observable<any> {
    return this.api.delete(`${this.url}/${id}`);
  }

}